/**
 * Proposta ganha cujo fecho não chegou todo ao Odoo (um anexo falhou, ou a
 * nota com o relatório): aviso com o que falta e «Reenviar para o Odoo», que
 * repete o `win_proposal` com o fecho guardado (`retry: true`) — anexa só o
 * que falta e deixa a nota (ou uma nota curta com os anexos que faltavam).
 */
import { useState } from "react"
import { Loader2, RefreshCw } from "lucide-react"
import type { AppShell } from "@/App"
import { Button } from "@/components/ui/button"

interface WonFileRef { key?: string; name?: string }

export function WonOdooStatus({ shell, proposal, onDone }: {
  shell: AppShell | null
  proposal: Record<string, unknown>
  onDone?: () => void
}) {
  const [busy, setBusy] = useState(false)
  if (proposal.status !== "ganho" || typeof proposal.won_report !== "string" || proposal.odoo_order_id == null) return null
  const files = [
    proposal.won_pdf_json as WonFileRef | null,
    proposal.won_bill_json as WonFileRef | null,
    ...((Array.isArray(proposal.won_photos_json) ? proposal.won_photos_json : []) as WonFileRef[]),
  ].filter((f): f is WonFileRef => !!f?.key)
  const state = (proposal.won_odoo_json ?? {}) as { attachments?: Record<string, number>; note_posted_at?: string }
  const pending = files.filter((f) => !state.attachments?.[f.key!])
  const noteMissing = !state.note_posted_at
  if (pending.length === 0 && !noteMissing) return null

  async function resend() {
    if (!shell) return
    setBusy(true)
    try {
      const res = await shell.client.post<{ success: boolean; message?: string; error?: string; data?: { odoo_error: string | null } }>(
        "actions/win_proposal",
        { params: { proposal_id: proposal.id, retry: true } },
      )
      if (!res.success) throw new Error(res.error || "Falha a reenviar")
      shell.bridge.toast({ variant: res.data?.odoo_error ? "error" : "success", description: res.message ?? "Fecho reenviado para o Odoo" })
      onDone?.()
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : "Falha a reenviar" })
    } finally {
      setBusy(false)
    }
  }

  const what = [
    noteMissing ? "a nota com o relatório" : null,
    pending.length ? `${pending.length === 1 ? "o anexo" : `${pending.length} anexos`} ${pending.map((f) => f.name).filter(Boolean).join(", ")}` : null,
  ].filter(Boolean).join(" e ")
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
      <span className="flex-1">Fecho incompleto no Odoo: falta {what}.</span>
      <Button size="sm" variant="outline" onClick={resend} disabled={busy || !shell}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        Reenviar para o Odoo
      </Button>
    </div>
  )
}
